import { ChangeEvent, useEffect, useState } from 'react';
import FormField from '@/common/PassengerTools/FormField';
import { useIsNative } from '~/context/common/DeviceContext';
import {
  Button as StyledButton,
  Input,
  Select
} from '../PassengerTools.styles';
import { storeTestUserInLocalStorage } from '../utils/createTestUser';
import {
  PARTNER_OPTIONS,
  PartnerId,
  createCreditKarmaLead,
  isLocalHost
} from '../utils/partners';

export default function PartnersSection() {
  const isNative = useIsNative();
  const [errorMessage, setErrorMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [partner, setPartner] = useState<PartnerId>('credit-karma');
  const [showSection, setShowSection] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [vehicleId, setVehicleId] = useState('');

  useEffect(() => {
    setShowSection(isLocalHost());
  }, []);

  async function handleGenerate() {
    setErrorMessage('');
    setSuccessMessage('');
    setIsGenerating(true);
    try {
      if (partner === 'credit-karma') {
        const { continueUrl, email, firstName } = await createCreditKarmaLead({
          vehicleId: vehicleId || undefined
        });
        const password = `TestBot!${new Date().getFullYear()}`;
        storeTestUserInLocalStorage({
          createdAt: new Date().toISOString(),
          email,
          firstName,
          lastName: 'TELLIER',
          name: 'Credit Karma',
          password,
          type: 'common'
        });
        setSuccessMessage(
          `Saved ${email} to Existing Users. In the new tab, activate with password ${password} so the saved login works — a different password won't match.`
        );
        if (!isNative)
          window.open(continueUrl, '_blank', 'noopener,noreferrer');

        return;
      }
      throw new Error(`Unsupported partner: ${partner}`);
    } catch (error) {
      setErrorMessage(`Failed to generate partner lead: ${error}`);
    } finally {
      setIsGenerating(false);
    }
  }

  if (!showSection) return null;

  return (
    <div className="flex flex-col items-start">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-8 rounded bg-gray-600 px-lg py-md text-white hover:bg-gray-700"
        data-analytics-id={null}
        data-testid="toggle-partners"
      >
        <span className="text-sm font-medium">Partners</span>
        <span className="text-sm">{isOpen ? '▾' : '▸'}</span>
      </button>

      {isOpen && (
        <div className="self-stretch flex flex-col gap-8 border border-gray-600 rounded-b p-sm">
          <FormField label="Partner" htmlFor="partner-select">
            <Select
              id="partner-select"
              value={partner}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setPartner(e.target.value as PartnerId)
              }
            >
              {PARTNER_OPTIONS.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField
            label="Vehicle ID (Optional)"
            htmlFor="partner-vehicle-id"
            helperText="Defaults to a known test vehicle if left blank."
          >
            <Input
              id="partner-vehicle-id"
              type="text"
              value={vehicleId}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setVehicleId(e.target.value)
              }
              placeholder="e.g. 1058063"
            />
          </FormField>

          {successMessage && (
            <div
              aria-live="polite"
              className="text-green-400 text-sm"
              role="status"
            >
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="text-[#feaaaa] text-sm" role="alert">
              {errorMessage}
            </div>
          )}

          <StyledButton
            type="button"
            onClick={handleGenerate}
            data-analytics-id={null}
            disabled={isGenerating}
            data-testid="generate-partner-lead"
          >
            {isGenerating ? 'Generating...' : 'Generate Lead & Continue'}
          </StyledButton>
        </div>
      )}
    </div>
  );
}
